import { createMuiTheme } from '@material-ui/core/styles';
import { ink, secondaryText, softGrey, darkGrey, boardBackground, ultraViolet } from '../common/palette';
import {
  amber,
  blue,
  blueGrey,
  brown,
  cyan,
  common,
  deepOrange,
  deepPurple,
  green,
  grey,
  lightBlue,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow,
} from '@material-ui/core/colors';
const SUPPORT_FONT_FAMILIES = [
  'AxLLCircular',
  'AxFFDIN',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
];

const COLOR_MAP = {
  amber,
  blue,
  blueGrey,
  brown,
  cyan,
  common,
  deepOrange,
  deepPurple,
  green,
  grey,
  lightBlue,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  red,
  teal,
  yellow,
};

interface Theme {
  palette?: {
    primary: string; // https://material-ui.com/customization/color/#color
    secondary: string; // https://material-ui.com/customization/color/#color
    error: string; // https://material-ui.com/customization/color/#color
    background: {
      paper: string;
      default: string;
    };
    text: {
      primary: string;
      secondary: string;
      disabled: string;
      hint: string;
    };
  };
  typography?: {
    htmlFontSize: number;
    fontSize: number;
    fontFamily: string;
    fontWeightLight: number;
    fontWeightRegular: number;
    fontWeightMedium: number;
    fontWeightBold: number;
    button: {
      textTransform: string;
      fontSize: number;
      fontWeight: number;
      lineHeight: number;
    };
  };
  shape?: {
    borderRadius: number;
  };
  zIndex?: {
    mobileStepper: number;
    appBar: number;
    drawer: number;
    modal: number;
    snackbar: number;
    tooltip: number;
  };
}

// A custom theme for this app
const theme = (props: Theme) => {
  const { palette, typography, shape } = props;
  return createMuiTheme({
    palette: {
      primary: COLOR_MAP[palette?.primary as 'deepPurple']
        ? COLOR_MAP[palette?.primary as 'deepPurple']
        : { ...deepPurple, '500': ultraViolet },
      secondary: COLOR_MAP[palette?.secondary as 'deepPurple'] ? COLOR_MAP[palette?.primary as 'deepPurple'] : grey,
      error: COLOR_MAP[palette?.error as 'deepPurple'] ? COLOR_MAP[palette?.primary as 'deepPurple'] : red,
      background: {
        paper: palette?.background?.paper || '#ffffff',
        default: palette?.background?.default || '#fafafa',
      },
      text: {
        primary: ink,
        secondary: darkGrey,
        disabled: softGrey,
        hint: boardBackground,
      },
    },
    typography: {
      htmlFontSize: 16,
      fontSize: typography?.fontSize || 14,
      fontFamily: typography?.fontFamily || SUPPORT_FONT_FAMILIES.join(','),
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      button: {
        textTransform: 'unset',
        fontSize: 14,
        fontWeight: 700,
        lineHeight: 2.15,
        borderRadius: 6,
      },
      h1: {
        fontSize: '24px',
        fontWeight: 700,
        lineHeight: 1.25,
        color: ink,
      },
      h2: {
        fontSize: '18px',
        fontWeight: 700,
        lineHeight: 1.25,
        color: ink,
      },
      h3: {
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: 1.25,
        color: ink,
      },
      h4: {
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: 1.25,
        color: ink,
      },
      h5: {
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 1.5,
        color: ink,
      },
      h6: {
        fontSize: '13px',
        fontWeight: 500,
        lineHeight: 1.5,
        color: ink,
      },
      body1: {
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: 1.5,
        color: ink,
      },
      body2: {
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: 1.5,
        color: ink,
      },
      // correspond to b1 subtitle
      subtitle1: {
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: 1.5,
        color: darkGrey,
      },
      // correspond to b1 disabled
      subtitle2: {
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: 1.5,
        color: secondaryText,
      },
    },
    shape: {
      borderRadius: shape?.borderRadius || 6,
    },
    zIndex: {
      mobileStepper: 1000,
      appBar: 1100,
      drawer: 1200,
      modal: 1300,
      snackbar: 1400,
      tooltip: 1500,
    },
  });
};

export default theme;
